/**
 * Signature picker of the composer toolbar (DESIGN.md T11): switch the signature in the body or remove it.
 * Central signatures carry a badge; a mandatory signature cannot be switched or removed (items disabled, note shown).
 */
import { LockIcon, PenLineIcon } from 'lucide-react';
import { Badge } from '../components/badge';
import { Button } from '../components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/dropdown-menu';

export interface ComposerSignatureOption {
  id: string;
  name: string;
  /** Rendered HTML inserted into the body. */
  html: string;
  /** Assigned by the organisation (read-only for the user). */
  central?: boolean | undefined;
}

export interface SignaturePickerLabels {
  /** Toolbar button and accessible name of the menu. */
  signature: string;
  none: string;
  /** Badge of central signatures. */
  central: string;
  /** Note when a mandatory signature fixes the choice. */
  locked: string;
}

export interface SignaturePickerProps {
  options: readonly ComposerSignatureOption[];
  /** Id in the body, undefined = none. */
  value: string | undefined;
  onChange: (option: ComposerSignatureOption | undefined) => void;
  locked?: boolean | undefined;
  labels: SignaturePickerLabels;
}

const NONE = '';

export function SignaturePicker({ options, value, onChange, locked = false, labels }: SignaturePickerProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" aria-label={labels.signature}>
          <PenLineIcon />
          <span className="hidden sm:inline">{labels.signature}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>{labels.signature}</DropdownMenuLabel>
        {locked && (
          <p className="flex items-center gap-1.5 px-2 pb-1.5 text-xs text-muted-foreground">
            <LockIcon aria-hidden className="size-3.5 shrink-0" />
            {labels.locked}
          </p>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={value ?? NONE} onValueChange={(id) => onChange(options.find((option) => option.id === id))}>
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.id} value={option.id} disabled={locked} className="gap-2">
              <span className="min-w-0 flex-1 truncate">{option.name}</span>
              {option.central && <Badge variant="info">{labels.central}</Badge>}
            </DropdownMenuRadioItem>
          ))}
          <DropdownMenuRadioItem value={NONE} disabled={locked}>
            {labels.none}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
